/* eslint-disable react-hooks/rules-of-hooks */
import React, {ChangeEvent, ChangeEventHandler, useEffect, useState,} from 'react';

import {Modal, ProgressBar} from 'react-bootstrap';

import {OrderBookingOption} from '../../types/Order';
import * as Service from '../../services/OrdersService';
import DollarOutput from '../utils/DollarOutput';
import {ActivitySource} from '../../types/Activity';
import {useFetchData} from '../../infrastructure/hooks/useFetchData';
import {OrderUpdateOption, SelectedOrderUpdateOption} from '../../types/OrderUpdateOption';
import {ApiStatus} from '../../infrastructure/constants/apiStatus';

import PriceBreakdown from "./PriceBreakdown";

export type ConfirmOrderPriceModalInputProps = {
  activityId: string;
  bookedOptions: OrderBookingOption[];
  source: ActivitySource;
  orderId: string;
  startDateTime: string;
  endDateTime: string;
};
type ConfirmOrderPriceModalProps = ConfirmOrderPriceModalInputProps & {
  onConfirm(
    result?: SelectedOrderUpdateOption
  ): void;
  onCancel: () => void;
};

type ConfirmOrderPriceModalOptionProps = {
  value: string;
  checked: boolean;
  label: React.ReactNode;
  smallLabel?: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

const ConfirmOrderPriceModalOption = (
  props: ConfirmOrderPriceModalOptionProps
) => {
  return (
    <div className={'confirm-order-price-modal__option'}>
      <input
        type='radio'
        id={`confirm-order-price-modal__${props.value}-option`}
        name='confirm-order-price-modal__option'
        value={props.value}
        checked={props.checked}
        onChange={props.onChange}
      />

      <div className={'confirm-order-price-modal__option-text'}>
        <label htmlFor={`confirm-order-price-modal__${props.value}-option`}>
          {props.label}
        </label>
        {props.smallLabel && <small>{props.smallLabel}</small>}
      </div>
    </div>
  );
};

const ConfirmOrderPriceModal = (props: ConfirmOrderPriceModalProps) => {
  const [selectedOption, setSelectedOption] = useState<
    SelectedOrderUpdateOption | undefined
  >(undefined);

  const [apiStatus, orderUpdateOptions , fetOrderUpdateOptions] = useFetchData<
    OrderUpdateOption[]
  >();

  const [priceBreakdownVisible, setPriceBreakdownVisible] = useState<boolean>(
    false
  );

  useEffect(() => {
    fetOrderUpdateOptions(() =>
      Service.getOrderUpdateOptions(
        props.activityId,
        props.bookedOptions,
        props.orderId,
        props.startDateTime,
        props.endDateTime,
      )
    );
  }, [
    fetOrderUpdateOptions,
    props.activityId,
    props.bookedOptions,
    props.orderId,
    props.startDateTime,
    props.endDateTime
  ]);

  useEffect(() => {
    if(apiStatus === ApiStatus.success && (orderUpdateOptions || []).length === 0)
    {
      props.onConfirm();
    }
  }, [orderUpdateOptions, apiStatus]);

  const handleConfirmClick = () => {
    if (selectedOption) {
      props.onConfirm(selectedOption);
    }
  };

  const handleCancelChangesClick = () => {
    props.onCancel();
  };

  const handlePriceBreakdownClick = () => {
    setPriceBreakdownVisible(priceBreakdownVisible => !priceBreakdownVisible);
  };

  if (apiStatus === ApiStatus.loading) {
    return (
      <Modal show size='lg' className={'confirm-order-price-modal'}>
        <Modal.Header className={"a"}>
          <b>Confirm the new total price</b>
        </Modal.Header>
        <Modal.Body>
          <p>Calculating new order price, please wait...</p>
          <ProgressBar active now={100} />
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show size='lg' className={`confirm-order-price-modal ${priceBreakdownVisible && 'confirm-order-price-modal--breakdown-shown'}`}>
      <Modal.Header>
        <b>Confirm the new total price</b>
        {selectedOption && (
          <button
            type='button'
            className='btn btn-link pull-right price-breakdown-link'
            onClick={handlePriceBreakdownClick}
          >
            {priceBreakdownVisible ? 'Hide' : 'Show'} Price Breakdown
          </button>
        )}
      </Modal.Header>
      <Modal.Body>
        {apiStatus === ApiStatus.error && <p>An error occurred while getting order update options</p>}
        {apiStatus === ApiStatus.success && (
          <>
            <section className={'confirm-order-price-modal__content'}>
             {(orderUpdateOptions || []).map(item => (
             <ConfirmOrderPriceModalOption
               key={item.id}
               value={item.id}
               checked={selectedOption?.id === item.id}
               label={
                 <>
                   <span>{`${item.name}: `}</span>
                   <span
                     className={'confirm-order-price-modal__option-message'}>{item.ledgerEntry.totalAmount > 0 ? "Additionally charge" : "Refund"} user </span>
                   <DollarOutput
                     value={Math.abs(item.ledgerEntry.totalAmount / 100).toFixed(2)}
                   />
                 </>
               }
               onChange={(e: ChangeEvent<HTMLInputElement>) =>
                 setSelectedOption({
                   id: e.target.value,
                   ledgerEntry: item.ledgerEntry,
                   totalAmount: item.ledgerEntry.totalAmount,
                 })
               }
             />
             ))}
           </section>
            {priceBreakdownVisible && selectedOption && (
              <section className='confirm-order-price-modal__price-breakdown'>
                <PriceBreakdown entry={selectedOption.ledgerEntry} />
              </section>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {apiStatus === ApiStatus.success ? (
          <button
            className='btn btn-primary'
            type='button'
            disabled={!selectedOption}
            onClick={handleConfirmClick}
          >
            Confirm
          </button>
        ) : null}
        <button
          className='btn btn-secondary'
          type='button'
          onClick={handleCancelChangesClick}
        >
          Cancel changes
        </button>
      </Modal.Footer>
    </Modal>
  );
};
export default ConfirmOrderPriceModal;
